export class CookieManager {
  // A private registry to track cookies set by this class
  private static cookieRegistry = new Set<string>();

  // Set a cookie
  static setCookie(
    name: string,
    value: string | object,
    options: {
      expires?: number | Date;
      path?: string;
      domain?: string;
      secure?: boolean;
      sameSite?: 'Strict' | 'Lax' | 'None';
    } = {}
  ): boolean {
    const serializedValue = typeof value === 'object' ? JSON.stringify(value) : value;
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(serializedValue)};`;

    if (options.expires) {
      const expires = options.expires instanceof Date
        ? options.expires.toUTCString()
        : new Date(Date.now() + options.expires * 1000).toUTCString();
      cookieString += `Expires=${expires};`;
    }

    if (options.path) {
      cookieString += `Path=${options.path};`;
    }

    if (options.domain) {
      cookieString += `Domain=${options.domain};`;
    }

    if (options.secure) {
      cookieString += `Secure;`;
    }

    if (options.sameSite) {
      cookieString += `SameSite=${options.sameSite};`;
    }

    document.cookie = cookieString;

    // Track this cookie in the registry
    this.cookieRegistry.add(name);
    return true;
  }

  // Get a cookie
  static getCookie(name: string): string | object | null {
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
      const [key, value] = cookie.split('=');
      if (decodeURIComponent(key) === name) {
        try {
          return JSON.parse(decodeURIComponent(value));
        } catch {
          return decodeURIComponent(value);
        }
      }
    }
    return null;
  }

  // Delete a cookie
  static deleteCookie(name: string, options: { path?: string; domain?: string } = {}): void {
    CookieManager.setCookie(name, '', {
      expires: -1, // Set to a past date
      path: options.path,
      domain: options.domain,
    });

    // Remove from registry
    this.cookieRegistry.delete(name);
  }

  static deleteAllCookiesByName(name: string): void {
    const domainParts = window.location.hostname.split('.');
    const pathParts = location.pathname.split('/').filter(Boolean);

    // Attempt to delete for each domain permutation
    for (let i = 0; i < domainParts.length - 1; i++) {
      const domain = '.' + domainParts.slice(i).join('.');

      // Attempt to delete for each path permutation
      let path = '/';
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`;

      for (let j = 0; j < pathParts.length; j++) {
        path += pathParts[j] + '/';
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`;
      }
    }

    // Also attempt deleting without domain (current domain)
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;

    // Remove from registry
    this.cookieRegistry.delete(name);
  }

  // Clear only cookies set by this class
  static clearCookies(): void {
    for (const name of this.cookieRegistry) {
      CookieManager.deleteCookie(name);
    }
    this.cookieRegistry.clear();
  }

  static getAllKeys(): any {
    return this.cookieRegistry.keys;
  }
}